// Chart presentation shared by the on-screen dashboard (chart_controller.js) and the server-side
// renderer that draws the same charts for scheduled emails and PDF exports.
//
// Only what is identical in both belongs here: value formatting, label compaction, axis titling, tick
// density, and the colour palette. Interaction concerns (tooltips, hover modes, responsive sizing) stay
// in the Stimulus controller -- a static image has none of them.
//
// Sharing these specifically is about drift: tick formatting, date compaction, and label thinning are
// exactly where an emailed chart would quietly stop matching the dashboard if each renderer kept its
// own copy.

export const FORMATTERS = {
  integer: (v) => new Intl.NumberFormat().format(Math.round(v)),
  decimal: (v) =>
    new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v),
  // The JS half of ChartsHelper's "rate": one place, matching the scalar, so a tooltip does not round
  // 21.4 cars an hour to 21 while the tile beside it keeps the digit.
  rate: (v) =>
    new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(v),
  percentage: (v) =>
    `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(v)}%`,
  ratio_percentage: (v) =>
    `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(v * 100)}%`,
  // Whole-dollar currency's hover-precision sibling. Opt-in via tooltipFormatter, so axes keep
  // reading $70 while the hover says $70.31.
  currency_cents: (v) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v),
  currency: (v) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(v),
  duration: (v) => {
    const secs = Math.max(0, Math.round(v));
    const d = Math.floor(secs / 86400);
    const h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    const parts = [];
    if (d) parts.push(`${d}d`);
    if (h) parts.push(`${h}h`);
    if (m) parts.push(`${m}m`);
    if (s) parts.push(`${s}s`);
    return parts.length ? parts.join(" ") : "0s";
  },
};

export const formatterFor = (name) => FORMATTERS[name] || FORMATTERS.integer;

const WHOLE_UNIT_FORMATTERS = [FORMATTERS.integer, FORMATTERS.currency];
export const wholeUnitTicks = (format) =>
  WHOLE_UNIT_FORMATTERS.includes(format) ? { precision: 0 } : {};

export const X_LABEL_MAX_LENGTH = 20;

export const truncateLabel = (label) => {
  const str = String(label ?? "");
  return str.length > X_LABEL_MAX_LENGTH
    ? `${str.slice(0, X_LABEL_MAX_LENGTH - 3)}...`
    : str;
};

export const AXIS_TITLE = {
  font: { size: 11, weight: "400" },
  color: "#6b7280",
  padding: 2,
};

// The y title reads vertically, so its gap to the tick values is horizontal and needs a touch more.
export const Y_AXIS_TITLE = {
  ...AXIS_TITLE,
  padding: { top: 2, bottom: 2, left: 0, right: 3 },
};
// Secondary axis sits on the right, so the same gap mirrors to the other side.
export const Y1_AXIS_TITLE = {
  ...AXIS_TITLE,
  padding: { top: 2, bottom: 2, left: 3, right: 0 },
};

// Below this a series is invisible against the card, so a saved opacity is clamped rather than honored.
export const MIN_OPACITY = 0.2;
// Matches the chart_controller Stimulus default and the settings form: an unset opacity is 0.7, not 1.
export const DEFAULT_OPACITY = 0.7;

export const applyOpacity = (hex, opacity) => {
  if (opacity == null || opacity >= 1) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(MIN_OPACITY, opacity)})`;
};

// A single series owns the whole category, with the gap inside it.
export const BAR_GEOMETRY = {
  borderRadius: 4,
  categoryPercentage: 1.0,
  barPercentage: 0.8,
};

// Grouped series are a DIFFERENT geometry, not the same numbers: bars within a group sit nearly flush
// so the group reads as one unit, and the slack categoryPercentage leaves keeps groups apart. Sharing
// the single-series values here is what made emailed grouped bars narrower than the on-screen ones.
export const GROUPED_BAR_GEOMETRY = {
  borderRadius: 4,
  categoryPercentage: 0.9,
  barPercentage: 0.95,
};
export const LINE_TENSION = 0.3;

export const DEFAULT_COLOR = "#5C80E0";
export const LINE_BASE_COLOR = "#64748b";
export const SERIES_PALETTE = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#84cc16", "#f97316", "#14b8a6",
  "#6366f1", "#eab308", "#dc2626", "#0ea5e9", "#a855f7",
  "#22c55e", "#f43f5e", "#0891b2", "#d946ef", "#65a30d",
];

// Date-only, or a timestamp at exact midnight (what date_trunc('day') yields). A non-midnight time is
// left alone: same-day points would all compact to one tick and stop being tellable apart.
export const ISO_DATE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ]00:00(?::00(?:\.0+)?)?Z?)?$/;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Parsed from the string, not Date, to dodge the UTC-midnight-shifts-back-a-day trap.
export const compactDateLabel = (label, { withYear = false } = {}) => {
  const match = ISO_DATE.exec(label);
  if (!match) return null;
  const [, year, month, day] = match;
  const monthName = MONTHS[Number(month) - 1];
  if (!monthName) return null;
  return withYear
    ? `${monthName} ${Number(day)}, ${year}`
    : `${monthName} ${Number(day)}`;
};

export const labelsSpanMultipleYears = (labels) => {
  const years = new Set();
  labels.forEach((label) => {
    const match = ISO_DATE.exec(String(label ?? ""));
    if (match) years.add(match[1]);
  });
  return years.size > 1;
};

// Thinning decisions count distinct labels: multi-series data repeats each label once per series, so a
// row count would call a 10-date two-series chart dense when the axis has only 10 ticks.
export const xTickDensity = (labels) => {
  const dense =
    labels.length > 10 && labels.every((label) => ISO_DATE.test(label));
  return {
    autoSkip: dense || labels.length > 12,
    autoSkipPadding: dense ? 40 : 6,
    maxTicksLimit: dense ? 8 : 12,
  };
};

// ---------------------------------------------------------------------------
// Canvas plugins. These live here, not in the controller, so the emailed and exported charts draw target
// lines and value labels with the SAME code the dashboard does. Both are pure canvas work with no DOM or
// hover dependency, so they run unchanged headless.
const TARGET_LINE_COLOR = "#7e8a9c";
// Matches the axis ticks rather than standing out, so a labelled chart reads as one type system.
const LABEL_FONT_PX = 10;
const LABEL_FONT = `400 ${LABEL_FONT_PX}px 'Helvetica Neue', Helvetica, Arial, sans-serif`;
const LABEL_GAP_PX = 3;
// Gap, glyph, and a gap of clearance — what a label needs to sit outside a mark.
export const LABEL_ROOM_PX = LABEL_GAP_PX + LABEL_FONT_PX + LABEL_GAP_PX;

export const valueLabelsPlugin = {
  id: "valueLabels",
  afterDatasetsDraw(chart, _args, opts) {
    if (!opts?.enabled) return;
    const { ctx } = chart;
    ctx.save();
    ctx.font = LABEL_FONT;
    ctx.textAlign = "center";
    ctx.fillStyle = "#6b7280";
    const { bottom } = chart.chartArea;
    chart.data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden) return;
      meta.data.forEach((element, j) => {
        const raw = dataset.data[j];
        if (raw === null || raw === undefined) return;
        const text = opts.format ? opts.format(raw, i) : String(raw);
        // Bars narrower than their own label are left unlabelled, as Metabase does; line points have
        // no width to measure and always draw.
        const slot = element.width;
        if (slot !== undefined && ctx.measureText(text).width > slot) return;
        // A stacked segment's neighbour occupies the space just outside it, so its label sits centred
        // inside its own band instead — and is dropped when the band is too short to hold the text.
        if (opts.stacked && element.base !== undefined) {
          const band = Math.abs(element.base - element.y);
          if (band < LABEL_FONT_PX + LABEL_GAP_PX) return;

          ctx.textBaseline = "middle";
          ctx.fillText(text, element.x, (element.y + element.base) / 2);
          return;
        }
        // A negative bar's label goes under it, except where that would reach the x-axis tick labels.
        const descending = element.base !== undefined && element.y > element.base;
        const under = descending && bottom - element.y >= LABEL_ROOM_PX;
        ctx.textBaseline = under ? "top" : "bottom";
        ctx.fillText(text, element.x, under ? element.y + LABEL_GAP_PX : element.y - LABEL_GAP_PX);
      });
    });
    ctx.restore();
  },
};

export const targetLinesPlugin = {
  id: "targetLines",
  afterDatasetsDraw(chart, _args, opts) {
    const lines = opts?.lines;
    if (!lines?.length) return;
    const { ctx, chartArea, scales } = chart;
    if (!scales.y) return;
    ctx.save();
    ctx.strokeStyle = TARGET_LINE_COLOR;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.font = "600 11px sans-serif";
    ctx.textBaseline = "bottom";
    ctx.textAlign = "right";
    lines.forEach(({ value, label }) => {
      const y = scales.y.getPixelForValue(value);
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
      if (label) {
        const x = chartArea.right - 6;
        const labelY = y - 6;
        ctx.setLineDash([]);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "rgba(255,255,255,0.9)";
        ctx.strokeText(label, x, labelY);
        ctx.fillStyle = TARGET_LINE_COLOR;
        ctx.fillText(label, x, labelY);
        ctx.strokeStyle = TARGET_LINE_COLOR;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 4]);
      }
    });
    ctx.restore();
  },
};
